/**
 * 메뉴 트리 노드 데이터
 * @typedef {object} MenuTreeNode
 * @property {string} menuId - 메뉴 ID
 * @property {string} title - 메뉴명
 * @property {string} type - 메뉴 타입
 * @property {string} [path] - 메뉴 경로
 * @property {string} [component] - 컴포넌트명
 * @property {string} [subType] - 서브 타입
 * @property {boolean} [mainDisplay] - 메인 노출 여부
 * @property {object} [options] - 옵션 데이터
 * @property {Array<object>} [form] - Form 데이터
 * @property {Array<MenuTreeNode>} [children] - 하위 메뉴
 */


/**
 * Tree 구조 데이터
 * @typedef {object} TreeStructureNode
 * @property {string} menuId - 메뉴 ID
 * @property {boolean} active - 활성 상태
 * @property {boolean} isParent - 부모 여부
 * @property {number} depth - depth 값
 * @property {Array<MenuTreeNode>} trail - 부모 경로 배열
 */


/**
 * 정렬용 Tree 메뉴 데이터
 * @typedef {object} SortTreeMenuNode
 * @property {string} id - 메뉴 ID
 * @property {string} parent - 부모 ID
 * @property {string} text - 메뉴명
 * @property {string} type - 메뉴 타입
 * @property {object} raw - 원본 데이터
 * @property {string} [component] - 컴포넌트명
 * @property {string} [subType] - 서브 타입
 * @property {boolean} [mainDisplay] - 메인 노출 여부
 * @property {Array<object>} [form] - Form 데이터
 * @property {object} [options] - 옵션 데이터
 */


/**
 * menuId 기준 메뉴 검색
 * @param {Array<MenuTreeNode>} tree - 메뉴 트리
 * @param {string} id - 찾을 menuId
 * @returns {MenuTreeNode|null}
 */
export const setFindItemId = (tree, id) => {
    for (const node of tree) {
        if (node?.menuId === id) return node;
        if (Array.isArray(node?.children) && node.children.length) {
            const found = setFindItemId(node.children, id);
            if (found) return found;
        }
    }
    return null;
}

/**
 * 현재 활성 메뉴 데이터 반환
 * @param {Array<MenuTreeNode>} list - 메뉴 리스트
 * @param {string|undefined} menuId - 메뉴 ID
 * @param {string|undefined} path - 메뉴 path
 * @returns {TreeStructureNode|Array}
 */
export const getCurrentNode = (list, menuId = undefined, path = undefined) => {
    const data = setTreeStructure(menuId, list);
    let activeItem = null;
    if (menuId !== undefined && menuId !== null) {
        activeItem = data.find(item => item.menuId === menuId);
    } else if (path) {
        activeItem = data.find(item => item.path === path);
    }
    if (!activeItem) return [];
    return activeItem;
};

/**
 * 비활성 메뉴 필터링
 * @param {Array<MenuTreeNode>} nodes - 메뉴 리스트
 * @returns {Array<MenuTreeNode>}
 */
export const setFilterActiveMenuList = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    const result = [];
    for (const node of nodes) {
        if (node && node.options && node.options.isActive === false) {
            continue;
        }
        const copy = { ...node };
        if (Array.isArray(node.children)) {
            const filteredChildren = setFilterActiveMenuList(node.children);
            if (filteredChildren.length > 0) {
                copy.children = filteredChildren;
            } else {
                delete copy.children;
            }
        }
        result.push(copy);
    }
    return result;
}

/**
 * 홈페이지내 메뉴 페이지 경로 생성
 * @param {string} id - 메뉴 ID
 * @param {string|undefined} component - 컴포넌트명
 * @param {string} basePath - 기본 경로
 * @param {string|undefined} subPath - 서브 경로
 * @returns {string}
 */
export const setMakePagePath = (id, component = undefined, basePath, subPath = undefined) => {
    let menuId;
    let mainUrl = "", subUrl= "";
    if(id) menuId = `?menu-id=${id}`

    if(component){
        mainUrl = `/${component}`
    } else if(subPath){
        subUrl = `/${subPath}`
    }
    return `/homepage/${basePath}${mainUrl}${subUrl}${menuId}`
}

/**
 * 메뉴 트리 구조 평탄화
 * @param {string|undefined} currentMenuId - 현재 메뉴 ID
 * @param {Array<MenuTreeNode>} items - 메뉴 리스트
 * @param {number} depth - depth 값
 * @param {Array<MenuTreeNode>} trail - 부모 경로 배열
 * @returns {Array<TreeStructureNode>}
 */
export const setTreeStructure = (currentMenuId = undefined, items, depth = 1, trail = []) => {
    if (!items) return [];
    let result = [];

    for (const item of items) {
        const isParent = !!item.children?.length;
        const currentTrail = [...trail, item];
        let isActive = currentMenuId === item.menuId;

        let childResults = [];
        if (isParent) {
            childResults = setTreeStructure(currentMenuId, item.children, depth + 1, currentTrail);
            if (childResults.find(child => child.active)) {
                isActive = true;
            }
        }
        result.push({
            ...item,
            active: isActive,
            isParent,
            depth: depth,
            trail: currentTrail,
        });
        if (isParent) {
            result.push(...childResults);
        }
    }
    return result;
}

/**
 * 메뉴 트리 정렬 데이터 생성
 * @param {Array<MenuTreeNode>} data - 메뉴 데이터
 * @param {string} parent - 부모 ID
 * @param {Array<SortTreeMenuNode>} list - 결과 리스트
 * @returns {Array<SortTreeMenuNode>}
 */
export const setSortTreeMenu = (data, parent, list) => {
    data.forEach((item) => {
        const node = {
            id: item.menuId,
            parent,
            text: item.title,
            type: item.type,
            raw: item,
        };
        if (item.component) {
            node.component = item.component;
        }
        if (item.subType) {
            node.subType = item.subType;
        }
        if (item.mainDisplay) {
            node.mainDisplay = item.mainDisplay;
        }
        if (item.form) {
            node.form = item.form;
        }
        if (item.options) {
            node.options = item.options;
        }
        list.push(node);

        if (Array.isArray(item.children) && item.children.length) {
            setSortTreeMenu(item.children, item.menuId, list);
        }
    });
    return list;
}